import React from 'react';
import { CountryCode } from '@dayetopia/library/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import NewsletterFormAlt from '@components/shared/NewsletterFormAlt';
import { BodyLG, BodySM } from '@components/shared/TextTypes';
import TwoColumn from '@components/shared/TwoColumn';
import { PageSlice, SliceNewsletterDetails } from '@contracts';

interface Props {
  slice: PageSlice;
  country: CountryCode;
}

const NewsletterPPCSubscribe = ({ slice, country }: Props) => {
  const sliceDetails = slice.primary as SliceNewsletterDetails;
  const { type } = sliceDetails;
  const { t } = useTranslation();

  const getListId = () => {
    if (type === 'hpv-waitlist') {
      return 'Y8CprN';
    }

    if (type === 'period-pain-waitlist') {
      if (country === CountryCode.US) {
        return 'VjVucc';
      }
      return 'RHbt2b';
    }

    return 'Y3UKy6';
  };

  const getTitle = () => {
    if (type === 'period-pain-waitlist' || type === 'hpv-waitlist') {
      return 'Join our waitlist now!';
    }

    return 'Short on time?';
  };

  const getBodyText = () => {
    if (type === 'hpv-waitlist') {
      return `Enter your email and be the first to know when we launch at-home HPV Screen.`;
    }

    if (type === 'period-pain-waitlist') {
      return `Enter your email and be the first to know when we launch in the ${country === CountryCode.US ? 'US' : 'EU'}.`;
    }

    return `Want to find out more about your period pain, but don't have time right now? Leave us your email and we'll send a link to your inbox for later.`;
  };

  return (
    <Container $background={type === 'hpv-waitlist' ? Colors.cream : Colors.ecru50}>
      <Inner>
        <TwoColumn gap="60px" breakpoint="1024px" alignment="center">
          <TextColumnWrap>
            <Title>{getTitle()}</Title>
            <StyledBodyLG>{getBodyText()}</StyledBodyLG>
          </TextColumnWrap>
          <EmailColumnWrap>
            <NewsletterFormAlt listId={getListId()} textColor={Colors.navyBlue} buttonText={t('components.send')} />
            <StyledBodySM>{t('components.vms-founders-results.note')}</StyledBodySM>
          </EmailColumnWrap>
        </TwoColumn>
      </Inner>
    </Container>
  );
};

export default NewsletterPPCSubscribe;

const Container = styled.div<{ $background?: string }>`
  background: ${(p) => p.$background || Colors.ecru50};
  padding: 60px;

  @media (max-width: 1024px) {
    padding: 30px;
  }
`;

const Inner = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const TextColumnWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 20px;
  max-width: 420px;
  color: ${Colors.navyBlue};

  @media (max-width: 1024px) {
    align-items: center;
  }
`;

const EmailColumnWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 20px;
  max-width: 420px;
  color: ${Colors.navyBlue};

  @media (max-width: 1024px) {
    align-items: center;
    padding-top: 40px;
  }
`;

const Title = styled.h3`
  font-family: 'Domaine';
  font-weight: 600;
  font-size: 24px;
  line-height: 140%;
  color: ${Colors.navyBlue};

  @media (max-width: 1024px) {
    text-align: center;
    font-size: 24px;
  }
`;

const StyledBodyLG = styled(BodyLG)`
  text-align: left;
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;

  @media (max-width: 1024px) {
    text-align: center;
    margin-bottom: 0px;
  }
`;

const StyledBodySM = styled(BodySM)`
  color: #001d3b;
  opacity: 0.5;
  @media (max-width: 1024px) {
    text-align: center;
  }
`;
